import React, { useContext, useEffect, useMemo } from "react";
import {
  useTable,
  useSortBy,
  useFilters,
  useGlobalFilter,
  usePagination,
} from "react-table";
import ColumnFilter from "./ColumnFilter";
import GlobalFilter from "./GlobalFilter";
import { FaEye } from "react-icons/fa";
import { AiOutlineFilePdf } from "react-icons/ai";
import logo from "../../../assets/logo.avif";
import { LoginContext } from "../../../reactcontext/ReactContext";

function ProfileTable({ data, columns, setActiveTab, setSelectedProfile, statusFilter, setStatusFilter, allProfiles }) {
  const { loginInfo } = useContext(LoginContext);

  const defaultColumn = useMemo(() => ({ Filter: ColumnFilter }), []);

  const tableHooks = (hooks) => {
    hooks.visibleColumns.push((columns) => [
      ...columns,
      {
        Header: "Actions",
        Footer: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="flex justify-center">
            <button onClick={() => handleDetails(row.original)}>
              <FaEye fontSize={24} color="green" />
            </button>
          </div>
        ),
        disableFilters: true,
      },
    ]);
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    pageCount,
    setPageSize,
    prepareRow,
    state,
    setGlobalFilter,
  } = useTable(
    { columns, data, defaultColumn },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    tableHooks
  );

  const { globalFilter, pageIndex } = state;

  const handleDetails = (record) => {
    setSelectedProfile(record);
    setActiveTab(2);
  };

  useEffect(() => { setPageSize(20); }, []);

  function getReadableTimestamp() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
  }

  const printedBy = loginInfo?.userDetails
    ? `${loginInfo.userDetails.firstName} ${loginInfo.userDetails.lastName}`
    : 'Admin';

  const exportPDF = () => {
    if (!data || data.length === 0) { alert('No data available to export.'); return; }
    const html2pdf = require('html2pdf.js');
    const tdStyle = 'padding:4px 6px;border:1px solid #e5e7eb;';
    const rows = data.map((r, i) => `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'}">
        <td style="${tdStyle}">${r.firstName} ${r.lastName}</td>
        <td style="${tdStyle}">${r.Passports?.[0]?.passportNo || '—'}</td>
        <td style="${tdStyle}">${r.email}</td>
        <td style="${tdStyle}">${r.nationality || '—'}</td>
        <td style="${tdStyle}">${r.mobile || '—'}</td>
        <td style="${tdStyle}">${r.Visas?.[0]?.expiryDate ? new Date(r.Visas[0].expiryDate).toLocaleDateString() : '—'}</td>
        <td style="${tdStyle}">${r.status}</td>
      </tr>`).join('');
    const el = document.createElement('div');
    el.style.cssText = 'padding:20px;font-family:Arial,sans-serif;font-size:11px;';
    el.innerHTML = `
      <div style="text-align:center;margin-bottom:14px;">
        <img src="${logo}" style="width:60px;display:block;margin:0 auto 6px auto;" crossorigin="anonymous" />
        <div style="font-size:16px;font-weight:bold;margin:4px 0;">Rahman Group</div>
        <div style="font-size:13px;margin:2px 0;">Report Listing - Employee List</div>
      </div>
      <div style="display:flex;gap:30px;flex-wrap:wrap;margin-bottom:12px;">
        <span><em>Printed By:</em> <strong>${printedBy}</strong></span>
        <span><em>Printed On:</em> <strong>${getReadableTimestamp()}</strong></span>
        <span><em>Total Records:</em> <strong>${data.length}</strong></span>
        <span><em>Signature:</em> <strong>______________</strong></span>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:10px;">
        <thead><tr style="background:#e5e7eb;">
          <th style="padding:5px 6px;border:1px solid #d1d5db;text-align:left;">Full Name</th>
          <th style="padding:5px 6px;border:1px solid #d1d5db;text-align:left;">Passport No</th>
          <th style="padding:5px 6px;border:1px solid #d1d5db;text-align:left;">Email</th>
          <th style="padding:5px 6px;border:1px solid #d1d5db;text-align:left;">Nationality</th>
          <th style="padding:5px 6px;border:1px solid #d1d5db;text-align:left;">Mobile</th>
          <th style="padding:5px 6px;border:1px solid #d1d5db;text-align:left;">Visa Expiry</th>
          <th style="padding:5px 6px;border:1px solid #d1d5db;text-align:left;">Status</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
    html2pdf().set({
      margin: 10,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
    }).from(el).outputPdf('blob').then(blob => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    });
  };

  return (
    <div className="w-full py-4 px-4 md:px-6">
      {/* Toolbar */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        {/* Search */}
        <div className="relative w-full sm:w-72">
          <GlobalFilter
            filter={globalFilter}
            setFilter={setGlobalFilter}
            placeholderText="Search Employee"
            className="w-full pl-10 pr-4 py-2 rounded-full shadow focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-700 text-sm border border-gray-300 bg-white"
          />
          <div className="absolute top-1/2 left-3 -translate-y-1/2 pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="10" cy="10" r="7" /><line x1="21" y1="21" x2="15" y2="15" />
            </svg>
          </div>
        </div>

        {/* Status filters + Export */}
        <div className="flex items-center gap-2 flex-wrap">
          {['All', 'Active', 'Inactive'].map(f => (
            <button key={f} onClick={() => setStatusFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                statusFilter === f
                  ? f === 'Active'   ? 'bg-green-500 text-white border-green-500'
                  : f === 'Inactive' ? 'bg-gray-500 text-white border-gray-500'
                                     : 'bg-black text-white border-black'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'
              }`}>
              {f} ({f === 'All' ? (allProfiles||data).length : (allProfiles||data).filter(p=>p.status===f).length})
            </button>
          ))}
          <button
            onClick={exportPDF}
            disabled={!data || data.length === 0}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md shadow font-medium ${
              !data || data.length === 0 ? 'bg-gray-300 cursor-not-allowed text-gray-500' : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
          >
            <AiOutlineFilePdf fontSize={16} /> Export PDF
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="w-full rounded-lg shadow bg-white border border-gray-200">
        <table {...getTableProps()} className="w-full table-fixed border-collapse text-xs sm:text-sm">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} className="bg-gray-100 text-left">
                {headerGroup.headers.map((col) => {
                  const hideBelow = col.id === 'passportNo' ? 'hidden md:table-cell'
                    : ['email', 'nationality'].includes(col.id) ? 'hidden lg:table-cell'
                    : '';
                  const colWidth = {
                    fullName: '20%', passportNo: '10%', email: '14%',
                    nationality: '9%', mobile: '11%', visaExpiryDate: '11%',
                    status: '16%', actions: '9%',
                  }[col.id] || 'auto';
                  return (
                    <th
                      {...col.getHeaderProps(col.getSortByToggleProps())}
                      style={{ width: colWidth }}
                      className={`sticky top-0 bg-gray-100 border-b border-gray-200 px-1 sm:px-2 lg:px-3 py-2 sm:py-3 text-gray-600 font-bold tracking-wider uppercase text-[10px] sm:text-xs leading-tight select-none break-words ${
                        hideBelow
                      } ${
                        col.id === 'actions' || col.id === 'status' ? 'text-center' : 'text-left'
                      }`}
                      key={col.id}
                    >
                      {col.render("Header")}
                      <span className="ml-0.5 text-gray-400">
                        {col.isSorted ? (col.isSortedDesc ? "▼" : "▲") : ""}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  className="border-t border-gray-100 hover:bg-gray-50 transition-colors duration-150"
                  key={row.id}
                >
                  {row.cells.map((cell) => {
                    const hideBelow = cell.column.id === 'passportNo' ? 'hidden md:table-cell'
                      : ['email', 'nationality'].includes(cell.column.id) ? 'hidden lg:table-cell'
                      : '';
                    return (
                      <td
                        {...cell.getCellProps()}
                        className={`px-1 sm:px-2 lg:px-3 py-2 sm:py-3 text-gray-700 truncate overflow-hidden ${hideBelow} ${
                          cell.column.id === 'actions' || cell.column.id === 'status' ? 'text-center' : ''
                        }`}
                        key={cell.column.id}
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-2 pt-3 pb-1 text-sm text-gray-600">
        <span>Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong></span>
        <div className="flex gap-1">
          <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="px-2 py-1.5 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-40 text-xs">{"<<"}</button>
          <button onClick={() => previousPage()} disabled={!canPreviousPage} className="px-3 py-1.5 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-40 text-xs">Prev</button>
          <button onClick={() => nextPage()} disabled={!canNextPage} className="px-3 py-1.5 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-40 text-xs">Next</button>
          <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} className="px-2 py-1.5 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-40 text-xs">{">>"}</button>
        </div>
      </div>
    </div>
  );
}

export default ProfileTable;
